import { useState, useCallback } from "react";
import API_ROUTES from "./apiRoutes";

const getToken = () => localStorage.getItem("token");

// Converte a resposta bruta do fetch numa mensagem de erro amigável.
// 401/403 recebem um texto específico (precisa ser admin), pois as rotas
// /admin exigem login + role admin.
const readError = async (response) => {
    if (response.status === 401 || response.status === 403) {
        return "Acesso negado. Você precisa estar logado como administrador para ver esta área.";
    }
    let data = null;
    try {
        data = await response.json();
    } catch {
        // corpo vazio ou não-JSON
    }
    return (data && (data.message || data.error)) || `Erro na requisição (${response.status}).`;
};

const authHeaders = () => ({
    "Authorization": `Bearer ${getToken()}`,
    "Content-Type": "application/json",
});

/**
 * Hook do painel de super admin (Usuários & Assinaturas).
 * Fala apenas com os 4 endpoints /admin do backend, sempre com Bearer token.
 */
export const useAdminSubscriptions = () => {
    // Stats (cards de resumo)
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsError, setStatsError] = useState(null);

    // Lista de assinantes (tabela)
    const [subscribers, setSubscribers] = useState([]);
    const [count, setCount] = useState(0);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState(null);

    // Detalhe do usuário (modal)
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);

    // Rodar expiração
    const [expiryRunning, setExpiryRunning] = useState(false);

    const fetchStats = useCallback(async () => {
        setStatsLoading(true);
        setStatsError(null);
        try {
            const response = await fetch(API_ROUTES.ADMIN.stats(), {
                method: "GET",
                headers: authHeaders(),
            });
            if (!response.ok) {
                throw new Error(await readError(response));
            }
            const data = await response.json();
            setStats(data);
        } catch (err) {
            setStatsError(err.message || "Erro ao carregar estatísticas.");
        } finally {
            setStatsLoading(false);
        }
    }, []);

    const fetchSubscribers = useCallback(async ({ search = "", plan = "", page = 1, size = 10 } = {}) => {
        setListLoading(true);
        setListError(null);
        try {
            const params = new URLSearchParams();
            if (search) params.set("search", search);
            if (plan) params.set("plan", plan);
            params.set("page", page);
            params.set("size", size);

            const url = `${API_ROUTES.ADMIN.subscribers()}?${params.toString()}`;
            const response = await fetch(url, {
                method: "GET",
                headers: authHeaders(),
            });
            if (!response.ok) {
                throw new Error(await readError(response));
            }
            const data = await response.json();
            setSubscribers(Array.isArray(data.users) ? data.users : []);
            setCount(data.count || 0);
        } catch (err) {
            setListError(err.message || "Erro ao carregar usuários.");
            setSubscribers([]);
            setCount(0);
        } finally {
            setListLoading(false);
        }
    }, []);

    const fetchUserDetail = useCallback(async (id) => {
        setDetailLoading(true);
        setDetailError(null);
        setDetail(null);
        try {
            const response = await fetch(API_ROUTES.ADMIN.userDetail(id), {
                method: "GET",
                headers: authHeaders(),
            });
            if (!response.ok) {
                throw new Error(await readError(response));
            }
            const data = await response.json();
            setDetail(data);
        } catch (err) {
            setDetailError(err.message || "Erro ao carregar detalhes do usuário.");
        } finally {
            setDetailLoading(false);
        }
    }, []);

    const clearDetail = useCallback(() => {
        setDetail(null);
        setDetailError(null);
        setDetailLoading(false);
    }, []);

    // Dispara a rede de segurança que expira assinaturas vencidas.
    // Retorna o resultado { expired, usersRecomputed } ou lança erro.
    const runExpiry = useCallback(async () => {
        setExpiryRunning(true);
        try {
            const response = await fetch(API_ROUTES.ADMIN.expiryRun(), {
                method: "POST",
                headers: authHeaders(),
            });
            if (!response.ok) {
                throw new Error(await readError(response));
            }
            return await response.json();
        } finally {
            setExpiryRunning(false);
        }
    }, []);

    return {
        // stats
        stats,
        statsLoading,
        statsError,
        fetchStats,
        // lista
        subscribers,
        count,
        listLoading,
        listError,
        fetchSubscribers,
        // detalhe
        detail,
        detailLoading,
        detailError,
        fetchUserDetail,
        clearDetail,
        // expiração
        expiryRunning,
        runExpiry,
    };
};
